"use client";
/**
 * NavFragmentSlot — client wrapper that renders the shell's navigation bar from
 * the published navigation fragment (`GET /api/fragments/nav`) instead of the
 * in-app `<NavigationBar>`.
 *
 * Purpose: a local, in-shell preview of the exact fragment output a migrated
 * zone would inject. It exercises the full loop — the shell renders the fragment
 * at `/api/fragments/nav`, then this client wrapper fetches and injects it —
 * without standing up a separate zone app. The server enables it for merchants
 * covered by the corresponding zone rollout.
 *
 * Inputs: `slug` (which merchant's nav to fetch), the header's scroll state, and
 * an optional `revalidateKey` cart-freshness signal (§9). The current route
 * (`usePathname`) is forwarded to the endpoint so the fragment's secondary
 * category bar, active-link highlighting, and compact scrolled layout track the
 * in-app nav exactly. Output: a container div whose innerHTML is the
 * script-stripped fragment markup.
 *
 * NOTE ON THE HOOK: the SDK's `useNavigationFragment` only forwards `slug`
 * (`?slug=`), so a fragment fetched through it always renders with
 * `pathName=null` and the library's `SecondaryNavigation` returns null. To reach
 * parity with the shell's route-aware nav we call `useFragment` directly — the
 * documented lower-level hook it wraps — and build a source URL carrying both
 * `slug` and `pathName`, with the route in the dep list so a navigation
 * re-fetches the correct secondary bar. `useFragment` still script-strips the
 * HTML before render, so no extra XSS guard is needed here.
 * `suppressHydrationWarning` is required because raw HTML is injected on both
 * the server and client render.
 */
import { useFragment, NAV_FRAGMENT_ENDPOINT } from "@wse/fragments";
import { useAuth } from "@/app/providers/authProvider";
import { useMerchant } from "@/app/providers/merchantProvider";
import { Perks } from "@/components/navigationBar/perks";
import { SearchField } from "@/components/navigationBar/searchField";
import { UtilityPanel } from "@/components/navigationBar/utilityPanel";
import { useCart } from "@/contexts/CartContext";
import { useCartDrawer } from "@/contexts/CartDrawerContext";
import {
  NAV_ACTION,
  NAV_SLOT,
  FEATURE_FLAG_LOCALIZATION_ON_COLO2,
} from "@/utils/constants";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import { useDepositPolicy } from "@/utils/useDepositPolicy";
import { useNativeActions } from "@/utils/useNativeActions";
import { useMerchantColor } from "@/utils/hooks/useMerchantColor";
import {
  CustomerDetails,
  EarnedPerks,
} from "@/dataLayer/getCustomerDetails";
import { GetMerchantPreferencesResponse } from "@/dataLayer/getMerchantPreferences";
import {
  getSearchRoute,
  getShowPerks,
  shouldShowPerksPanel,
} from "@/utils/navigationBarUtils";
import { shouldHardNavigate } from "@/utils/hardNavigation";
import { createPortal } from "react-dom";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { memo, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { useNavActionBindings } from "./useNavActionBindings";
import { useMobileDrawerController } from "./useMobileDrawerController";
import { useNavSlotPortal } from "./useNavSlotPortal";
import { LanguageSelector } from "@/components/languageSelector/languageSelector";
import { useMerchantFeatureFlag } from "@/utils/useMerchantFeatureFlag";
import { useAuthLifecycle } from "@/utils/hooks/useAuthLifecycle";
import useMediaQuery from "@/utils/useMediaQuery";
import { OrderType } from "@/constants/enum";

interface NavFragmentSlotProps {
  slug: string;
  initialHtml?: string;
  isScrolledDown?: boolean;
  /** Optional cart signal; bump on cart change to refresh the badge (§9). */
  revalidateKey?: string | number;
  customerPerks?: EarnedPerks[];
  merchantPreferences?: GetMerchantPreferencesResponse;
  onDayPartMenuChange?: (id: string) => void;
  deleteSession: () => Promise<void>;
  isScanToOrderFlow?: boolean;
}

interface FragmentMarkupProps {
  containerRef: RefObject<HTMLDivElement | null>;
  html: string;
}

const FragmentMarkup = memo(function FragmentMarkup({
  containerRef,
  html,
}: FragmentMarkupProps) {
  return (
    <div
      ref={containerRef}
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

export function NavFragmentSlot({
  slug,
  initialHtml,
  isScrolledDown = false,
  revalidateKey,
  customerPerks = [],
  merchantPreferences,
  onDayPartMenuChange,
  deleteSession,
  isScanToOrderFlow = false,
}: Readonly<NavFragmentSlotProps>) {
  const { merchant } = useMerchant();
  const { isLoggedIn } = useAuth();
  const { cart } = useCart();
  const { toggleCartDrawer } = useCartDrawer();
  const { isCmaApp, openLoginSignUp } = useNativeActions();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const { push, refresh } = useRouter();
  const [searchFieldOpen, setSearchFieldOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [perksModalIsOpen, setPerksModalIsOpen] = useState(false);
  const [loginModalIsOpen, setLoginModalIsOpen] = useState(false);
  const [toCheckoutAfterLogin, setToCheckoutAfterLogin] = useState(false);
  const [customerDetails, setCustomerDetails] = useState<
    Partial<CustomerDetails> | null
  >(null);
  const prevSession = useRef(isLoggedIn);
  const merchantColor = useMerchantColor();
  const isMobileScreen = useMediaQuery("(max-width: 640px)");
  const localizationEnabled = useMerchantFeatureFlag(
    merchant,
    FEATURE_FLAG_LOCALIZATION_ON_COLO2
  );
  const handleLoginModal = (toCheckout: boolean) => {
    setToCheckoutAfterLogin(toCheckout);
    if (isCmaApp) {
      openLoginSignUp(toCheckout ? "checkoutButton" : "profileIcon");
      return;
    }
    setLoginModalIsOpen(true);
  };

  const isDineIn =
    searchParams?.get("order_type")?.toLowerCase() ===
    OrderType.DINE_IN.toLowerCase();

  useAuthLifecycle({
    isLoggedIn,
    isDineIn,
    isScanToOrderFlow,
    deleteSession,
    refresh,
    merchantId: merchant.id,
    isMobileScreen,
    prevSession,
    setCustomerDetails,
  });

  const cartItemCount =
    cart?.items.reduce(
      (total, item) => total + (item.value?.quantity ?? 0),
      0
    ) ?? 0;
  const source =
    `${NAV_FRAGMENT_ENDPOINT}?slug=${encodeURIComponent(slug)}` +
    `&pathName=${encodeURIComponent(pathName ?? "")}` +
    `&isScrolledDown=${isScrolledDown}` +
    `&order_type=${encodeURIComponent(searchParams?.get("order_type") ?? "")}` +
    `&menu=${encodeURIComponent(searchParams?.get("menu") ?? "")}` +
    `&cartValue=${cartItemCount}`;

  const cartRevalidateKey = revalidateKey ?? cartItemCount;
  const fragmentDeps = [
    slug,
    pathName,
    isScrolledDown,
    cartRevalidateKey,
    searchParams?.get("order_type") ?? "",
    searchParams?.get("menu") ?? "",
  ];
  const { renderableHtml, containerRef, isLoading } = useFragment({
    sources: [source],
    initialHtml,
    deps: fragmentDeps,
  });

  const mobileDrawer = useMobileDrawerController(containerRef, [
    ...fragmentDeps,
    renderableHtml,
  ]);

  const handleSearch = (event: { key: string }) => {
    if (event.key !== "Enter") return;
    const params = new URLSearchParams(searchParams || "");
    if (searchQuery) params.set("query", searchQuery);
    else params.delete("query");
    const query = params.toString();
    const destination = (route: string) => `${route}?${query}`;

    const { route, requiresHardNavigation } = getSearchRoute(pathName, merchant);
    const searchDestination = destination(route);
    if (
      requiresHardNavigation ||
      shouldHardNavigate(searchDestination, merchant)
    ) {
      globalThis.location.assign(searchDestination);
    } else {
      push(searchDestination);
    }
    setSearchFieldOpen(false);
  };

  useNavActionBindings(
    containerRef,
    {
      onSearch: () => setSearchFieldOpen(true),
      onCart: toggleCartDrawer,
      onMobileMenu: () => mobileDrawer.open(),
      onMobileClose: () => mobileDrawer.close(),
      onDayPart: (id) => onDayPartMenuChange?.(id),
    },
    [...fragmentDeps, renderableHtml]
  );

  useEffect(() => {
    setSearchFieldOpen(false);
    setSearchQuery("");
    if (globalThis.location.href?.includes("#cart")) {
      toggleCartDrawer();
    }
  }, [pathName]);

  const perksPortalTarget = useNavSlotPortal(containerRef, NAV_SLOT.PERKS, [
    ...fragmentDeps,
    renderableHtml,
  ]);
  const languagePortalTarget = useNavSlotPortal(
    containerRef,
    NAV_SLOT.LANGUAGE,
    [
      ...fragmentDeps,
      renderableHtml,
    ]
  );
  const accountPortalTarget = useNavSlotPortal(containerRef, NAV_SLOT.ACCOUNT, [
    ...fragmentDeps,
    renderableHtml,
  ]);
  useEffect(() => {
    const searchAction = containerRef.current?.querySelector<HTMLElement>(
      `[data-wse-nav-action="${NAV_ACTION.SEARCH}"]`
    );
    if (!searchAction) return;

    searchAction.hidden = searchFieldOpen;
    return () => {
      searchAction.hidden = false;
    };
    // The injected fragment key controls which search action is updated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, searchFieldOpen, renderableHtml, ...fragmentDeps]);
  const tableData = getFromLocalStorage(LocalStorageKey.TABLE_DATA, undefined);
  const refreshedCustomerPerks = Array.isArray(customerDetails?.earnedPerks)
    ? customerDetails.earnedPerks
    : customerPerks;
  const depositMightApply = useDepositPolicy(merchant, merchantPreferences);
  const showPerks = getShowPerks(
    merchant,
    merchantPreferences,
    depositMightApply
  );
  const shouldRenderPerks = shouldShowPerksPanel(
    isLoggedIn,
    refreshedCustomerPerks,
    showPerks,
    merchant,
    tableData,
    isCmaApp
  );

  if (!renderableHtml && !isLoading) return null;

  return (
    <>
      <div className="relative">
        <FragmentMarkup containerRef={containerRef} html={renderableHtml ?? ""} />
        {searchFieldOpen && (
          <div className="absolute inset-x-0 top-0 z-50 bg-white">
            <SearchField
              searchParams={searchParams}
              setSearchQuery={setSearchQuery}
              handleSearch={handleSearch}
              setSearchFieldOpen={setSearchFieldOpen}
            />
          </div>
        )}
      </div>
      {shouldRenderPerks &&
        perksPortalTarget &&
        createPortal(
          <Perks
            handlePerksModal={() => setPerksModalIsOpen(!perksModalIsOpen)}
            perksModalIsOpen={perksModalIsOpen}
            customerPerks={refreshedCustomerPerks}
          />,
          perksPortalTarget
        )}
      {languagePortalTarget &&
        createPortal(
          <LanguageSelector localizationEnabledFlag={localizationEnabled} />,
          languagePortalTarget
        )}
      {accountPortalTarget &&
        createPortal(
          <UtilityPanel
            handleLoginModal={handleLoginModal}
            loginModalIsOpen={loginModalIsOpen}
            setLoginModalIsOpen={setLoginModalIsOpen}
            deleteSession={deleteSession}
            merchantColor={merchantColor}
            toCheckoutAfterLogin={toCheckoutAfterLogin}
            merchant={merchant}
          />,
          accountPortalTarget
        )}
    </>
  );
}
